import {
  petAppointmentsFailure,
  petAppointmentsRequest,
  petAppointmentsSuccess,
} from 'store/reducers/petInformation';
import {
  providerAppointmentsFailure,
  providerAppointmentsRequest,
  providerAppointmentsSuccess,
} from 'store/reducers/providerAppointments';
import { hashData } from 'util';

import { fetchData } from './fetchData';

export const acceptAppointment = (appointmentId) => (dispatch, getState) =>
  fetchData({
    endpoint: `appointment/${appointmentId}/provider/accept`,
    actions: [null, null, null],
    httpMethod: 'post',
  })(dispatch, getState);

export const arrivedAppointment = (appointmentId) => (dispatch, getState) =>
  fetchData({
    endpoint: `appointment/${appointmentId}/provider/arrived`,
    actions: [null, null, null],
    httpMethod: 'post',
  })(dispatch, getState);

export const declineAppointment = (appointmentId) => (dispatch, getState) =>
  fetchData({
    endpoint: `appointment/${appointmentId}/provider/decline`,
    actions: [null, null, null],
    httpMethod: 'post',
  })(dispatch, getState);

export const rescheduleAppointment =
  ({ appointmentId, body }) =>
  (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${appointmentId}/provider/suggest`,
      actions: [null, null, null],
      httpMethod: 'post',
      body,
    })(dispatch, getState);

export const noShowAppointment =
  (appointmentId, waiveFee) => (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${appointmentId}/provider/no-show?waive_fee=${!!waiveFee}`,
      actions: [null, null, null],
      httpMethod: 'post',
    })(dispatch, getState);

export const cancelAppointment =
  (appointmentId, waiveFee) => (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${appointmentId}/provider/cancel?waive_fee=${!!waiveFee}`,
      actions: [null, null, null],
      httpMethod: 'post',
    })(dispatch, getState);

export const patchAppointment =
  (appointmentId, body, skipEmail = false) =>
  (dispatch, getState) => {
    const skipEmailQuery = skipEmail ? '?skip_email=True' : '';
    return fetchData({
      endpoint: `appointment/${appointmentId}/provider${skipEmailQuery}`,
      actions: [null, null, null],
      httpMethod: 'patch',
      body,
    })(dispatch, getState);
  };

export const createAppointment =
  ({
    animalUuid,
    locationUuid,
    locationUserUuid,
    serviceUuid,
    userUuid,
    note,
    dateStart,
    appointmentSource = 'new',
  }) =>
  (dispatch, getState) => {
    const { source } = getState().main.appointment;
    const sourceValue =
      appointmentSource === 'rebook'
        ? 'web provider rebook'
        : 'web provider new apt';
    const sourceId = source.find((s) => s.value === sourceValue)?.id;

    const uuidObject = {
      animal: animalUuid,
      location: locationUuid,
      location_user: locationUserUuid,
      service: serviceUuid,
      user: userUuid,
    };

    return fetchData({
      endpoint: 'appointment/provider',
      actions: [null, null, null],
      httpMethod: 'post',
      body: {
        uuid: uuidObject,
        dateStart: `${dateStart}.000Z`,
        note,
        sourceId,
      },
    })(dispatch, getState);
  };

export const loadAppointmentsRequests =
  (animalUuid) => (dispatch, getState) => {
    if (!animalUuid) return;
    const luid = getState().provider.locations[0].uuid;
    return fetchData({
      endpoint: `location/${luid}/animals/${animalUuid}/appointments`,
      actions: [
        petAppointmentsRequest,
        petAppointmentsSuccess,
        petAppointmentsFailure,
      ],
    })(dispatch, getState);
  };

export const loadAppointment =
  ({ apuid, actions }) =>
  (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${apuid}/provider`,
      actions: actions || [null, null, null],
    })(dispatch, getState);

export const invoiceSummary =
  ({ apuid, body }) =>
  (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${apuid}/provider/summary`,
      actions: [null, null, null],
      httpMethod: 'post',
      body,
    })(dispatch, getState);

export const completeAppointment =
  ({ apuid, body }) =>
  (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${apuid}/provider/completed`,
      actions: [null, null, null],
      httpMethod: 'post',
      body,
    })(dispatch, getState);

export const loadAvailableSlots =
  ({ dateStart, dateEnd, groomer, luid }, actions) =>
  (dispatch, getState) => {
    const dateQuery = `?date_start=${dateStart}&date_end=${dateEnd}`;
    const userQuery = `&user_uuid=${groomer}`;

    const queries = `${dateQuery}${userQuery}`;

    return fetchData({
      endpoint: `location/${luid}/availability/consumer${queries}`,
      actions: actions || [null, null, null],
      httpMethod: 'get',
    })(dispatch, getState);
  };

export const loadClients = (search) => (dispatch, getState) => {
  const { provider } = getState();
  const luid = provider.locations?.[0]?.uuid;

  return fetchData({
    endpoint: `location/${luid}/consumers?q=${search}`,
    actions: [null, null, null],
  })(dispatch, getState);
};

export const loadClientPaymentData =
  ({ uuid }) =>
  (dispatch, getState) => {
    const { provider } = getState();
    const luid = provider.locations?.[0]?.uuid;

    return fetchData({
      endpoint: `location/${luid}/consumers/${uuid}`,
      actions: [null, null, null],
    })(dispatch, getState);
  };

export const loadInvoiceCheck = (service) => (dispatch, getState) => {
  const { provider } = getState();
  const luid = provider.locations?.[0]?.uuid;

  return fetchData({
    endpoint: 'appointment/invoice/check',
    actions: [null, null, null],
    httpMethod: 'post',
    body: {
      service,
      location: luid,
    },
  })(dispatch, getState);
};

export const loadTodaysAppointments = () => (dispatch, getState) => {
  const { provider, ui } = getState();
  const luid = provider.locations?.[0]?.uuid;
  const locationUserUuid = ui.selectedGroomer.uuid;

  return fetchData({
    endpoint: `location/${luid}/appointments/today${
      locationUserUuid && locationUserUuid !== 'all'
        ? `?user_uuid=${locationUserUuid}`
        : ''
    }`,
    actions: [providerAppointmentsRequest, null, providerAppointmentsFailure],
  })(dispatch, getState).then(({ success, data }) => {
    if (success) {
      const res = data.data;
      hashData(res).then((hash) => {
        dispatch(providerAppointmentsSuccess({ data: res, hash }));
      });
      return { success, data: res };
    }
  });
};

export const notifyClient =
  ({ apuid, message }) =>
  (dispatch, getState) =>
    fetchData({
      endpoint: `appointment/${apuid}/provider/notify`,
      actions: [null, null, null],
      httpMethod: 'post',
      body: { message },
    })(dispatch, getState);

export const getDayAppointments =
  ({ date, groomer }) =>
  (dispatch, getState) => {
    const luid = getState().provider.locations[0].uuid;
    if (!date || !groomer || !luid) return null;
    return fetchData({
      endpoint: `location/${luid}/appointments/calendar?date_start=${date}&date_end=${date}&user_uuid=${groomer}`,
      actions: [null, null, null],
    })(dispatch, getState).then(({ success, data }) => {
      if (success) return data?.data?.calendar?.[0]?.appointments;
      return null;
    });
  };

export const getDayUnavailability =
  ({ date, groomer }) =>
  (dispatch, getState) => {
    const luid = getState().provider.locations[0].uuid;
    if (!date || !groomer || !luid) return null;
    return fetchData({
      endpoint: `location/${luid}/user/${groomer}/inventory?date_start=${date}&date_end=${date}`,
      actions: [null, null, null],
    })(dispatch, getState).then(({ success, data }) => {
      if (success) {
        const unavailability = data?.calendar?.[0].unavailable;
        const onlyUnavailability = unavailability.map((timeBlock) => ({
          ...timeBlock.date,
          uuid: timeBlock.uuid,
        }));
        return onlyUnavailability;
      }
      return null;
    });
  };
